import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
import http from "@/helper/http";
import { RESOURCE_CACHE, queryKeys } from "@/config/queryConfig";

// ==================== GROUPS ====================

export function useStoreGroups(storeId, options = {}) {
  const storeIdValue = computed(() => {
    return typeof storeId === "object" && storeId.value !== undefined
      ? storeId.value
      : storeId;
  });

  return useQuery({
    queryKey: computed(() => queryKeys.groups(storeIdValue.value)),
    queryFn: async () => {
      const id = storeIdValue.value;
      const response = await http.get(`/v2/stores/${id}/groups`);
      return response.data.items || response.data || [];
    },
    enabled: computed(() => !!storeIdValue.value),
    ...RESOURCE_CACHE.groups,
    ...options,
  });
}

// ==================== PROMO CODES ====================

export function usePromoCodes(storeId, options = {}) {
  const storeIdValue = computed(() => {
    // Handle both direct values and computed refs
    return typeof storeId === "object" && storeId.value !== undefined
      ? storeId.value
      : storeId;
  });

  return useQuery({
    queryKey: computed(() => queryKeys.promoCodes(storeIdValue.value)),
    queryFn: async () => {
      const id = storeIdValue.value;
      const response = await http.get(`/v1/store/promo_codes/store/${id}`);
      return response.data;
    },
    enabled: computed(() => !!storeIdValue.value),
    ...RESOURCE_CACHE.promoCodes,
    ...options,
  });
}

// ==================== PROMO CODE USAGE ====================

export function useStorePromoUsage(storeId, filters = {}, options = {}) {
  const storeIdValue = computed(() => {
    return typeof storeId === "object" && storeId.value !== undefined
      ? storeId.value
      : storeId;
  });

  const filtersValue = computed(() => {
    return typeof filters === "object" && filters.value !== undefined
      ? filters.value
      : filters;
  });

  return useQuery({
    queryKey: computed(() =>
      queryKeys.storePromoUsage(storeIdValue.value, filtersValue.value)
    ),
    queryFn: async () => {
      const id = storeIdValue.value;
      const currentFilters = filtersValue.value;
      const params = {
        page: currentFilters.page || 1,
        per_page: currentFilters.perPage || 10,
      };

      if (currentFilters.startDate) {
        params.start_date = currentFilters.startDate;
      }
      if (currentFilters.endDate) {
        params.end_date = currentFilters.endDate;
      }

      const response = await http.get(`/v1/stores/${id}/promo-code-usage`, {
        params,
      });
      return response.data;
    },
    enabled: computed(() => !!storeIdValue.value),
    ...RESOURCE_CACHE.promoUsage,
    ...options,
  });
}

export function useGroupPromoUsage(groupId, filters = {}, options = {}) {
  const groupIdValue = computed(() => {
    return typeof groupId === "object" && groupId.value !== undefined
      ? groupId.value
      : groupId;
  });

  const filtersValue = computed(() => {
    return typeof filters === "object" && filters.value !== undefined
      ? filters.value
      : filters;
  });

  return useQuery({
    queryKey: computed(() =>
      queryKeys.groupPromoUsage(groupIdValue.value, filtersValue.value)
    ),
    queryFn: async () => {
      const id = groupIdValue.value;
      const currentFilters = filtersValue.value;
      const params = {
        page: currentFilters.page || 1,
        per_page: currentFilters.perPage || 10,
      };

      if (currentFilters.startDate) {
        params.start_date = currentFilters.startDate;
      }
      if (currentFilters.endDate) {
        params.end_date = currentFilters.endDate;
      }

      const response = await http.get(`/v2/groups/${id}/promo-code-usage`, {
        params,
      });
      return response.data;
    },
    enabled: computed(() => !!groupIdValue.value),
    ...RESOURCE_CACHE.promoUsage,
    ...options,
  });
}

// ==================== MUTATIONS ====================

export function useCreatePromoCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ storeId, payload }) => {
      const response = await http.post(
        `/v1/store/promo_codes/${storeId}`,
        payload
      );
      return response.data;
    },
    onSuccess: (data, { storeId }) => {
      queryClient.invalidateQueries({ queryKey: ["promoCodes", storeId] });
    },
  });
}

export function useUpdatePromoCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ promoCodeId, payload, storeId }) => {
      const response = await http.patch(
        `/v1/store/promo_codes/${promoCodeId}`,
        payload
      );
      // Extract store_id from response data if not provided
      const responseStoreId = storeId || response.data?.store_id;
      return { data: response.data, storeId: responseStoreId };
    },
    onSuccess: (result) => {
      const { storeId } = result;
      if (storeId) {
        // Invalidate specific store's promo codes
        queryClient.invalidateQueries({ queryKey: ["promoCodes", storeId] });
      } else {
        // If no storeId found, invalidate all promo codes
        queryClient.invalidateQueries({ queryKey: ["promoCodes"] });
      }
    },
  });
}

export function useActivatePromoCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ promoCodeId, type, storeId }) => {
      const response = await http.post(
        `/v1/store/promo_codes/${promoCodeId}/${type}`
      );
      // Extract store_id from response data if not provided
      const responseStoreId = storeId || response.data?.store_id;
      return { data: response.data, storeId: responseStoreId };
    },
    onSuccess: (result) => {
      const { storeId } = result;
      if (storeId) {
        // Invalidate specific store's promo codes
        queryClient.invalidateQueries({ queryKey: ["promoCodes", storeId] });
      } else {
        // If no storeId found, invalidate all promo codes
        queryClient.invalidateQueries({ queryKey: ["promoCodes"] });
      }
    },
  });
}

export function useCopyPromoCodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await http.post(`/api/v1/promo_codes/copy`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promoCodes"] });
    },
  });
}

// ==================== HELPERS ====================

export function useInvalidatePromoCodes() {
  const queryClient = useQueryClient();

  return {
    invalidatePromoCodes: (storeId) =>
      queryClient.invalidateQueries({ queryKey: ["promoCodes", storeId] }),
    invalidateStoreUsage: (storeId) =>
      queryClient.invalidateQueries({ queryKey: ["storePromoUsage", storeId] }),
    invalidateGroupUsage: (groupId) =>
      queryClient.invalidateQueries({ queryKey: ["groupPromoUsage", groupId] }),
    invalidateAll: () =>
      queryClient.invalidateQueries({ queryKey: ["promoCodes"] }),
  };
}
